import { useIsFocused } from '@react-navigation/native';
import { SpineView } from '@wiener/rn-spine';
import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { activeSpineInstances, generateInstanceId } from '../utils/debug/instanceTracker';

interface SpineAnimationProps {
	skeletonFile: string;
	atlasFile: string;
	animationName?: string;
	skin?: string;
	loop?: boolean;
	playing?: boolean;
	width?: number;
	height?: number;
	sharedCanvasGroup?: string;
}

const SpineAnimation: React.FC<SpineAnimationProps> = ({
	skeletonFile,
	atlasFile,
	animationName,
	skin,
	loop,
	playing = true,
	width = 300,
	height = 300,
	sharedCanvasGroup,
}) => {
	const instanceIdRef = useRef(generateInstanceId('spine'));
	const isFocused = useIsFocused();
	const activePlaying = playing && isFocused;

	useEffect(() => {
		const id = instanceIdRef.current;
		console.log(
			`[RNSpine JS] 🚀 Mounting SpineAnimation [${id}] -\n` +
				`  skeletonFile: ${skeletonFile}\n` +
				`  atlasFile: ${atlasFile}\n` +
				`  animationName: ${animationName}\n` +
				`  sharedCanvasGroup: ${sharedCanvasGroup}`,
		);

		activeSpineInstances.set(id, {
			id,
			skeletonFile,
			playing: activePlaying,
			isFocused,
		});

		return () => {
			console.log(`[RNSpine JS] ❌ Unmounting SpineAnimation [${id}]`);
			activeSpineInstances.delete(id);
		};
	}, [activePlaying, animationName, atlasFile, isFocused, sharedCanvasGroup, skeletonFile]);

	// Keep dynamic states updated in the tracking Map
	useEffect(() => {
		const id = instanceIdRef.current;
		const current = activeSpineInstances.get(id);
		if (current) {
			current.playing = activePlaying;
			current.isFocused = isFocused;
			activeSpineInstances.set(id, current);
		}
	}, [activePlaying, isFocused]);

	if (process.env.NODE_ENV !== 'production') {
		console.log(
			`[RNSpine JS] ✏️ Rendering SpineAnimation [${instanceIdRef.current}] - playing: ${playing} | activePlaying (isFocused): ${activePlaying}`,
		);
	}

	const SpineViewComponent = SpineView as any;

	return (
		<View
			style={[styles.container, { width, height }]}
			onLayout={e => {
				if (process.env.NODE_ENV !== 'production') {
					const { width: lw, height: lh } = e.nativeEvent.layout;
					console.log(
						`[RNSpine JS] 📐 SpineAnimation [${instanceIdRef.current}] layout: ${lw}x${lh}`,
					);
				}
			}}
		>
			<SpineViewComponent
				skeletonFile={skeletonFile}
				atlasFile={atlasFile}
				animationName={animationName}
				skin={skin}
				loop={loop}
				playing={activePlaying}
				sharedCanvasGroup={sharedCanvasGroup}
				style={styles.spineView}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		justifyContent: 'center',
		alignItems: 'center',
		overflow: 'visible',
	},
	spineView: {
		width: '100%',
		height: '100%',
	},
});

export default SpineAnimation;
